import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useApproveGate,
  useArtifacts,
  useGates,
  useRejectGate,
  useSimulation,
  useValidation,
} from "@/api/queries";
import type { Gate } from "@/api/types";
import { ArtifactDetail } from "@/components/ArtifactDetail";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Panel, SectionTitle } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { Field, TextArea } from "@/components/Field";
import { ArtifactStatus, GateStatus, StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { formatPercent } from "@/lib/cn";
import { useUiStore } from "@/store/ui";

/**
 * 审核关口（specs/07 §4 · §7.2）。
 *
 * 规则：未通过上游关口，下游只能生成候选，不得标记正式（PR-126）。
 * 带例外通过必须填写理由，例外会随导出包一并交付。
 *
 * G-7 生产就绪指标：只读聚合关口健康、验证告警与六路径模拟结果，
 * 为 reviewer 批准 G7 提供量化证据；规则判定仍由后端确定性校验器负责。
 *
 * @dataSource remote
 */
export function ReviewView() {
  const navigate = useNavigate();
  const projectId = useUiStore((state) => state.projectId);
  const pushToast = useUiStore((state) => state.pushToast);
  const gates = useGates(projectId);
  const validation = useValidation(projectId);
  const simulation = useSimulation(projectId);
  const approve = useApproveGate(projectId);
  const reject = useRejectGate(projectId);

  const [target, setTarget] = useState<{ gate: Gate; mode: "approve" | "reject" } | null>(null);
  const [comment, setComment] = useState("");
  const [exception, setException] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);

  // 待审阅产物：关口是「阶段级」判断，具体是哪几份产物在等签字要看 artifact 状态。
  const submitted = useArtifacts(projectId, undefined, "submitted");

  // `/gates` 还会返回 PROJECT（当前阶段）、S8（计划中）与 P-1（上架审核）三行，
  // 前两者不是可审批关口；P-1 是关口但入口在发布面板，故与阶段关口分开呈现。
  const rows = (gates.data ?? []).filter((gate) => gate.gate.startsWith("G"));
  const publishGate = (gates.data ?? []).find((gate) => gate.gate === "P-1") ?? null;
  const projectRow = (gates.data ?? []).find((gate) => gate.gate === "PROJECT");
  const approvedCount = rows.filter((gate) => gate.status === "approved").length;
  const pending = rows.find((gate) => gate.status !== "approved") ?? null;

  // G-7 生产就绪证据（只读聚合，数据来自后端既有端点）
  const issues = validation.data?.issues ?? [];
  const errorCount = issues.filter((issue) => issue.severity === "error").length;
  const warningCount = issues.filter((issue) => issue.severity === "warning").length;
  const coverage = simulation.data?.coverage;
  const simPaths = simulation.data?.paths ?? [];
  const deadEnds = simPaths.filter((path) => path.failures.length > 0).length;
  const endingCoverage = coverage?.ending_coverage ?? {};
  const endingIds = Object.keys(endingCoverage);
  const reachableEndings = endingIds.filter((id) => (endingCoverage[id] ?? 0) > 0).length;
  const readinessBlocked = errorCount > 0 || deadEnds > 0;

  function submit() {
    if (!target) return;
    if (target.mode === "approve") {
      approve.mutate(
        { gate: target.gate.gate, comment, exceptionReason: exception },
        {
          onSuccess: () => {
            pushToast({
              title: `${target.gate.gate} 已通过`,
              detail: exception
                ? "已记录例外理由，将随导出包一并交付"
                : `${target.gate.title} · 下游可开始标记正式产物`,
              mark: "✓",
            });
            reset();
          },
        },
      );
    } else {
      reject.mutate(
        { gate: target.gate.gate, reason: comment || "未说明原因" },
        {
          onSuccess: () => {
            pushToast({
              title: `${target.gate.gate} 已驳回`,
              detail: "驳回记录会保留在关口历史中，供后续追溯",
              mark: "!",
            });
            reset();
          },
        },
      );
    }
  }

  function reset() {
    setTarget(null);
    setComment("");
    setException("");
  }

  return (
    <div className="page-scroll">
      <div className="page-width">
        {/*
          四个查询任一失败都必须整块呈现错误态（AC-1401 / RULES §11.2）。
          此前只有 `submitted` 有一行内联提示，而 `gates` / `validation` / `simulation`
          失败时页面照旧渲染「0 已通过 · 0 待处理」——那会被读成"这个项目就没有关口"，
          而不是"没读到"。
        */}
        <RemoteState queries={[gates, validation, simulation, submitted]} title="审核关口加载失败">
          <header className="page-heading">
            <div>
              <span className="eyebrow">G-1..G7 · 未过关不得标记正式</span>
              <h2>关口不是流程装饰，它决定哪一版内容可以被当真</h2>
              <p>
                每个关口绑定一类产物。通过意味着这批内容可以进入下一阶段；驳回会保留理由，
                并阻止下游把候选当作正式版本使用。
              </p>
            </div>
            <div className="meta-row">
              <Tag tone="green">{approvedCount} 已通过</Tag>
              <Tag tone={pending ? "gold" : "blue"}>{rows.length - approvedCount} 待处理</Tag>
              {projectRow ? <Tag tone="blue">当前阶段 {projectRow.stage ?? "—"}</Tag> : null}
            </div>
          </header>

          <SectionTitle title="关口清单" description="按顺序通过；跳过上游关口会被拒绝" />

          <div className="gate-list">
            {rows.map((gate) => (
              <div className="gate-row" key={gate.gate}>
                <strong>{gate.gate}</strong>
                <div>
                  <strong>{gate.title}</strong>
                  <small>
                    阶段 {gate.stage ?? "—"} · 绑定产物：
                    {gate.artifact_types.length
                      ? gate.artifact_types.join(" / ")
                      : "无（流程关口）"}
                    {gate.approved_by
                      ? ` · 由 ${gate.approved_by}（${gate.approved_by_role}）通过`
                      : ""}
                  </small>
                  {gate.comment ? <small>意见：{gate.comment}</small> : null}
                  {gate.rejections.length ? (
                    <small>
                      驳回 {gate.rejections.length} 次 · 最近：
                      {String(
                        (gate.rejections[0] as Record<string, unknown> | undefined)?.reason ?? "—",
                      )}
                    </small>
                  ) : null}
                </div>
                <div className="toolbar-right">
                  <GateStatus status={gate.status} />
                  {gate.approval_entry !== "gate" ? (
                    // 专用入口的关口（目前只有 G-1 PROFILE_LOCK）在本页**只展示、不批准**：
                    // 它的副作用是"锁定 Profile"，由项目分类向导负责（specs/07 §4 视图清单）。
                    // 若在此提供按钮，会走成一条"有记录、无副作用"的假批准，
                    // 从而绕过 Profile 的完整性校验（RULES §18）。
                    <Button
                      size="small"
                      variant="quiet"
                      onClick={() => navigate("/studio/classify")}
                    >
                      {gate.status === "approved" ? "查看分类" : "去分类向导锁定"}
                    </Button>
                  ) : gate.status === "approved" ? (
                    <Button
                      size="small"
                      variant="quiet"
                      onClick={() => setTarget({ gate, mode: "reject" })}
                    >
                      撤销
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="small"
                        variant="quiet"
                        onClick={() => setTarget({ gate, mode: "reject" })}
                      >
                        驳回
                      </Button>
                      <Button
                        size="small"
                        variant="primary"
                        onClick={() => setTarget({ gate, mode: "approve" })}
                      >
                        通过
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {publishGate ? (
              // P-1 上架审核（`ADR-022`）：本页**只展示 + 指路**。
              // 它的副作用是迁移 `publish_status`，唯一入口在项目总览的发布面板；
              // 在此提供按钮会走成"有记录、无状态"的假批准（RULES §18）。
              <div className="gate-row" key={publishGate.gate}>
                <strong>{publishGate.gate}</strong>
                <div>
                  <strong>{publishGate.title}</strong>
                  <small>
                    {"阶段 — · 绑定产物：无（流程关口）"}
                    {publishGate.approved_by
                      ? ` · 由 ${publishGate.approved_by}（${publishGate.approved_by_role}）通过`
                      : ""}
                  </small>
                  {publishGate.comment ? <small>意见：{publishGate.comment}</small> : null}
                  {publishGate.rejections.length ? (
                    <small>驳回 {publishGate.rejections.length} 次</small>
                  ) : null}
                </div>
                <div className="toolbar-right">
                  <GateStatus status={publishGate.status} />
                  <Button size="small" variant="quiet" onClick={() => navigate("/studio/overview")}>
                    去发布面板
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <SectionTitle
            title="待审阅产物"
            description="关口决定阶段能不能过；这里决定具体哪一版产物可以被当真（V102/V103）"
          />

          <Panel
            title="submitted 状态的产物"
            description="批准 / 退回在详情面板内执行，决定会留下审阅记录"
            className="community-funnel"
            bodyClassName="idea-list"
          >
            {(submitted.data ?? []).length === 0 ? (
              <p className="inline-note">当前没有等待审阅的产物。</p>
            ) : (
              <ul className="activity-list">
                {(submitted.data ?? []).map((artifact) => (
                  <li className="activity-item" key={artifact.id}>
                    <span className="mini-mark">¶</span>
                    <div>
                      <p>{artifact.title || artifact.logical_key}</p>
                      <small>
                        {artifact.type} · v{artifact.version} · {artifact.gate ?? "无绑定关口"}
                      </small>
                    </div>
                    <div className="story-card-actions">
                      <ArtifactStatus status={artifact.status} />
                      <Button
                        size="small"
                        variant="primary"
                        onClick={() => setDetailId(artifact.id)}
                      >
                        审阅
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <SectionTitle
            title="G-7 生产就绪指标"
            description="审批 G7 前的量化证据：关口健康、验证告警与六路径模拟（本页只呈现证据，不改变判定结果）"
          />

          <div className="growth-layout">
            <Panel title="关口健康" description="人工批准轨迹" bodyClassName="metric-rows">
              <div className="metric-row">
                <span className="metric-label">已通过</span>
                <strong className="metric-value">{approvedCount}</strong>
              </div>
              <div className="metric-row">
                <span className="metric-label">待处理</span>
                <strong className="metric-value">
                  {pending ? `${pending.gate} · ${pending.title}` : "0"}
                </strong>
              </div>
              <div className="metric-row">
                <span className="metric-label">例外交付</span>
                <strong className="metric-value">
                  {rows.some((gate) => gate.exception_reason) ? "有" : "无"}
                </strong>
              </div>
            </Panel>

            <Panel title="验证告警" description="最新校验结果" bodyClassName="metric-rows">
              <div className="metric-row">
                <span className="metric-label">阻断（error）</span>
                <strong className="metric-value">{errorCount}</strong>
              </div>
              <div className="metric-row">
                <span className="metric-label">警告（warning）</span>
                <strong className="metric-value">{warningCount}</strong>
              </div>
              <div className="metric-row">
                <span className="metric-label">其余（info）</span>
                <strong className="metric-value">
                  {Math.max(issues.length - errorCount - warningCount, 0)}
                </strong>
              </div>
              <div className="metric-row">
                <StatusLabel tone={readinessBlocked ? "warning" : "success"}>
                  {readinessBlocked ? "存在阻断告警" : "无阻断告警"}
                </StatusLabel>
              </div>
            </Panel>

            <Panel title="六路径模拟" description="S7 验证证据" bodyClassName="metric-rows">
              <div className="metric-row">
                <span className="metric-label">Storylet 覆盖</span>
                <strong className="metric-value">
                  {formatPercent(coverage?.storylet_coverage ?? 0, 1)}
                </strong>
              </div>
              <div className="metric-row">
                <span className="metric-label">选择覆盖</span>
                <strong className="metric-value">
                  {formatPercent(coverage?.choice_coverage ?? 0, 1)}
                </strong>
              </div>
              <div className="metric-row">
                <span className="metric-label">死路路径</span>
                <strong className="metric-value">{deadEnds}</strong>
              </div>
              <div className="metric-row">
                <span className="metric-label">结局可达</span>
                <strong className="metric-value">
                  {reachableEndings}/{endingIds.length || 0}
                </strong>
              </div>
            </Panel>
          </div>

          <Panel
            title="关口规则"
            description="为什么这里不能一键全部通过"
            className="community-funnel"
            bodyClassName="funnel"
          >
            <div className="funnel-step">
              <strong>PR-126 顺序约束</strong>
              <span>上游关口未通过时，下游产物只能停留在候选状态</span>
            </div>
            <div className="funnel-step">
              <strong>例外必须留痕</strong>
              <span>带例外通过需写明理由，随导出包交付并在审阅记录中可查</span>
            </div>
            <div className="funnel-step">
              <strong>撤销同样记账</strong>
              <span>已通过关口被撤销后，依赖它的下游产物会被一并标记为失效</span>
            </div>
          </Panel>
        </RemoteState>
      </div>

      <Modal
        open={Boolean(target)}
        onClose={reset}
        eyebrow={target ? `${target.gate.gate} · ${target.gate.title}` : ""}
        title={target?.mode === "approve" ? "通过关口" : "驳回关口"}
        footer={
          <>
            <Button variant="quiet" onClick={reset}>
              取消
            </Button>
            <Button
              variant={target?.mode === "approve" ? "primary" : "default"}
              disabled={target?.mode === "reject" && comment.trim().length === 0}
              onClick={submit}
            >
              {target?.mode === "approve" ? "确认通过" : "确认驳回"}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field
            id="gate-comment"
            label={target?.mode === "approve" ? "审阅意见" : "驳回原因"}
            required={target?.mode === "reject"}
          >
            <TextArea
              id="gate-comment"
              rows={3}
              value={comment}
              placeholder={
                target?.mode === "approve"
                  ? "例如：结构问题已收敛，可进入分场锁定"
                  : "例如：市集线场景数仍超预算，请合并后再提交"
              }
              onChange={(event) => setComment(event.target.value)}
            />
          </Field>
          {target?.mode === "approve" ? (
            <Field
              id="gate-exception"
              label="例外理由（可留空）"
              hint="只有存在未解决的告警但仍需推进时才填写；填写后将被记录为带例外通过"
            >
              <TextArea
                id="gate-exception"
                rows={2}
                value={exception}
                placeholder="例如：样片范围仅覆盖 A01-A03，R07 告警不影响本轮交付"
                onChange={(event) => setException(event.target.value)}
              />
            </Field>
          ) : null}
        </div>
      </Modal>

      <ArtifactDetail artifactId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
